import { createI18n } from 'next-international'
import React from 'react'
import { CountryEnum } from '@/types/enum/enumUser'

export enum Locale {
  FR = 'fr',
  'FR-BE' = 'fr-BE',
  EN = 'en',
  IT = 'it',
  'NL-BE' = 'nl-BE',
}

export const languagesAvailable = [
  {
    code: Locale.FR,
    name: 'Français',
    flag: CountryEnum.FRANCE,
    active: ({ siteBelgium, isUserAdminBackOffice, countries }) =>
      !siteBelgium && (isUserAdminBackOffice || countries?.includes(CountryEnum.FRANCE)),
  },
  {
    code: Locale['FR-BE'],
    name: 'Français',
    flag: CountryEnum.BELGIUM,
    active: ({ siteBelgium, countries }) => siteBelgium || countries?.includes(CountryEnum.BELGIUM),
  },
  {
    code: Locale['NL-BE'],
    name: 'Nederlands',
    flag: CountryEnum.BELGIUM,
    active: ({ siteBelgium, countries }) =>
      siteBelgium ||
      countries?.includes(CountryEnum.BELGIUM) ||
      countries?.includes(CountryEnum.LUXEMBOURG),
  },
  {
    code: Locale.EN,
    name: 'English',
    flag: CountryEnum.UNITED_KINGDOM,
    active: ({ countries }) => countries?.includes(CountryEnum.UNITED_KINGDOM),
  },
  {
    code: Locale.IT,
    name: 'Italiano',
    flag: CountryEnum.ITALY,
    active: ({ countries }) => countries?.includes(CountryEnum.ITALY),
  },
]
/**
 * DICTIONNAIRES EN DEUX ÉTAGES (campagne perf 2026-08, voir scripts/split-locales.cjs) :
 * next-international ne reçoit que le CORE (~40 % des clés : avant-login, shell,
 * patient) — le premier rendu n'attend plus le parse des ~9 500 clés. Les modules
 * métier (DEFERRED) sont fusionnés juste après le montage du shell via
 * loadDeferredLocale() ; useI18n les sert par un overlay transparent (même API).
 * Les fichiers locales/<locale>.ts restent la source de vérité — `yarn locales:split`
 * régénère locales/generated/ (contrôlé en pre-push).
 */
const {
  useI18n: originalUseI18n,
  useScopedI18n,
  I18nProvider,
  getLocaleProps,
  useCurrentLocale,
  useChangeLocale,
} = createI18n({
  en: () => import('./generated/en.core'),
  fr: () => import('./generated/fr.core'),
  'fr-BE': () => import('./generated/fr-BE.core'),
  it: () => import('./generated/it.core'),
  'nl-BE': () => import('./generated/nl-BE.core'),
  ze: () => import('./generated/ze.core'),
})

const DEFERRED_LOADERS: Record<string, () => Promise<{ default: Record<string, any> }>> = {
  en: () => import('./generated/en.deferred'),
  fr: () => import('./generated/fr.deferred'),
  'fr-BE': () => import('./generated/fr-BE.deferred'),
  it: () => import('./generated/it.deferred'),
  'nl-BE': () => import('./generated/nl-BE.deferred'),
  ze: () => import('./generated/ze.deferred'),
}

let deferredDict: Record<string, any> | null = null
let deferredLoadedFor: string | null = null
const deferredListeners = new Set<() => void>()

/**
 * Charge et active l'étage DIFFÉRÉ pour la locale donnée. Appelé par MainAppShell dès
 * son montage (PatientAppShell ne l'appelle PAS : monsuivipharma vit sur le core seul).
 * Idempotent par locale ; notifie tous les composants montés pour re-rendre une fois.
 */
export const loadDeferredLocale = async (locale: string) => {
  if (deferredLoadedFor === locale) return
  const loader = DEFERRED_LOADERS[locale] ?? DEFERRED_LOADERS.fr
  const mod = await loader()
  deferredDict = mod.default
  deferredLoadedFor = locale
  deferredListeners.forEach(listener => listener())
}

/** Interpolation compatible next-international : remplace chaque {param}. */
const interpolate = (value: string, params?: Record<string, unknown>): string => {
  if (!params) return value
  return value.replace(/\{(\w+)\}/g, (raw, name) =>
    params[name] === undefined ? raw : String(params[name])
  )
}

const pluralRulesCache = new Map<string, Intl.PluralRules>()

/**
 * Même règle que next-international (create-t.ts) : `count === 0` mappe toujours sur
 * `#zero`, sinon Intl.PluralRules décide. À garder aligné avec la lib, sinon les clés
 * du core et celles de l'étage différé ne se résoudraient pas pareil.
 */
const getPluralSuffix = (locale: string, count: number): string => {
  if (count === 0) return 'zero'
  let rules = pluralRulesCache.get(locale)
  if (!rules) {
    try {
      rules = new Intl.PluralRules(locale)
    } catch {
      rules = new Intl.PluralRules(defaultLocale)
    }
    pluralRulesCache.set(locale, rules)
  }
  return rules.select(count)
}

/**
 * Résout une clé de l'étage différé, variantes plurielles comprises : le dictionnaire
 * ne contient alors que `key#zero` / `key#one` / `key#other`, jamais `key` nue.
 */
const resolveDeferred = (
  key: string,
  locale: string,
  params?: Record<string, unknown>
): unknown => {
  if (!deferredDict) return undefined
  const direct = deferredDict[key]
  if (direct !== undefined) return direct
  const count = params?.count
  if (typeof count !== 'number') return undefined

  return (
    deferredDict[`${key}#${getPluralSuffix(locale, count)}`] ?? deferredDict[`${key}#other`]
  )
}

/**
 * useI18n avec overlay : les clés du core passent par next-international (inchangé),
 * celles de l'étage différé sont servies par l'overlay dès qu'il est chargé. Avant
 * chargement, une clé différée renvoie la clé brute (fenêtre de quelques dizaines de
 * ms au boot, sur des écrans qui attendent de toute façon leurs données).
 */
const useI18n: () => any = () => {
  const t = originalUseI18n()
  const locale = useCurrentLocale()
  const [, forceRender] = React.useReducer((x: number) => x + 1, 0)

  React.useEffect(() => {
    deferredListeners.add(forceRender)
    return () => {
      deferredListeners.delete(forceRender)
    }
  }, [])

  return React.useMemo(
    () =>
      (key: string, params?: Record<string, unknown>) => {
        const deferredValue = resolveDeferred(key, locale ?? defaultLocale, params)
        if (deferredValue !== undefined) {
          return typeof deferredValue === 'function'
            ? deferredValue(params)
            : interpolate(deferredValue as string, params)
        }
        return (t as any)(key, params)
      },
    // deferredLoadedFor est un module-level volontaire : le re-render vient du listener
    // (forceRender), la dep ne sert qu'à invalider le useMemo au chargement de l'étage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, deferredLoadedFor]
  )
}

export { useI18n, useScopedI18n, I18nProvider, getLocaleProps, useCurrentLocale, useChangeLocale }

export const defaultLocale = 'fr'
